import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { loveScore, LoveResult } from "../utils/loveScore";
import "./LoveCalculator.css";

interface LoveCalculatorProps {
  /**
   * Optional gate, run after the input is validated but before the result is
   * produced. Return true to allow it, false to stop so the caller can do
   * something else (open the auth modal).
   *
   * Left undefined on /love-calculator, which is a public SEO page and has to
   * stay usable logged out. Supplied on /love, a campaign page whose job is
   * the signup. Validation still runs first, so a visitor is never asked to
   * sign in only to find their input was rejected.
   */
  authGate?: () => boolean;
}

const LoveCalculator: React.FC<LoveCalculatorProps> = ({ authGate }) => {
  const [yourName, setYourName] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [result, setResult] = useState<LoveResult | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Clear the pending timer if the page unmounts mid-count, otherwise the
     callback fires against a component that no longer exists. */
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const calculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (calculating) return;

    const r = loveScore(yourName, partnerName);
    if (!r) {
      setError("Both names please, at least two letters each.");
      setResult(null);
      return;
    }

    /* Gate AFTER validation, so nobody is asked to sign in and then told their
       names were not accepted. The names stay in state while the modal is up,
       so the second click is all it takes. */
    if (authGate && !authGate()) {
      setError("");
      return;
    }

    /* The maths is instant, so the wait is deliberate. A number that appears
       the moment you click reads as canned; a couple of seconds reads as the
       tool having done something. Kept short on purpose. */
    setError("");
    setResult(null);
    setCalculating(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setResult(r);
      setCalculating(false);
    }, 2200);
  };

  return (
    <div className="lovecalc">
      <form className="lovecalc-form" onSubmit={calculate}>
        <div className="lovecalc-fields">
          <label className="lovecalc-field">
            <span>Your name</span>
            <input
              type="text"
              value={yourName}
              onChange={(e) => setYourName(e.target.value)}
              placeholder="Enter your name"
              autoComplete="off"
            />
          </label>

          <span className="lovecalc-heart" aria-hidden="true">
            &#10084;
          </span>

          <label className="lovecalc-field">
            <span>Partner's name</span>
            <input
              type="text"
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="Enter their name"
              autoComplete="off"
            />
          </label>
        </div>

        <button
          type="submit"
          className="lovecalc-submit"
          disabled={calculating}
        >
          {calculating ? "Reading the numbers\u2026" : "Calculate love percentage"}
        </button>
        {error ? <p className="lovecalc-error">{error}</p> : null}
      </form>

      {calculating ? (
        <div className="lovecalc-loading" role="status" aria-live="polite">
          <div className="lovecalc-hearts" aria-hidden="true">
            <span>&#10084;</span>
            <span>&#10084;</span>
            <span>&#10084;</span>
          </div>
          <p>Adding up the letters, comparing your numbers&#8230;</p>
        </div>
      ) : null}

      {result ? (
        <div className="lovecalc-result" role="status">
          <div className="lovecalc-score">
            <span className="lovecalc-percent">{result.percent}%</span>
            <span className="lovecalc-band">{result.headline}</span>
          </div>

          <div
            className="lovecalc-meter"
            role="img"
            aria-label={`Love percentage ${result.percent} out of 100`}
          >
            <span style={{ width: `${result.percent}%` }} />
          </div>

          <p className="lovecalc-detail">{result.detail}</p>

          <p className="lovecalc-numbers">
            Your name number is <strong>{result.yourNumber}</strong>, theirs is{" "}
            <strong>{result.partnerNumber}</strong>. The score is how those two
            sit together in Pythagorean numerology.
          </p>

          <Link to="/chat" className="lovecalc-cta">
            Ask Aura AI what your charts say
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default LoveCalculator;
